import type { EnvironmentRef, KeyIndexEntry, User } from '@fleequid-secrets/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Palette } from '@/components/Palette';
import { PasswordDialog } from '@/components/PasswordDialog';
import { Shortcuts } from '@/components/Shortcuts';
import { envStyle, MODULES, type ModuleName, TopBar } from '@/components/TopBar';
import { copyText } from '@/lib/clipboard';
import { typing } from '@/lib/hooks';
import { navigate, usePath } from '@/lib/router';
import { comparePath, parseRoute, vaultPath } from '@/lib/routes';
import { Cancelled, clearGo, type Command, goPending, markGo, type Shell, ShellContext, type ToastAction } from '@/lib/shell';
import { remember, remembered } from '@/lib/storage';
import { Activity } from '@/pages/Activity';
import { Compare } from '@/pages/Compare';
import { Environments } from '@/pages/Environments';
import { Placeholder } from '@/pages/Placeholder';
import { Team } from '@/pages/Team';
import { Vault } from '@/pages/Vault';

const QUIET_MS = 10 * 60_000;

interface PendingConfirm {
    environment: EnvironmentRef;
    what: string;
    resolve: (slug: string) => void;
    reject: (reason: Cancelled) => void;
}

// The shell around every page: the environment's edge, the top bar, the palette, the keyboard sheet, the toast, the
// confirmation a protected environment asks for, and what the pages share.
export function App(props: { user: User; onLogout: () => void }): ReactNode {
    const path = usePath();
    const route = parseRoute(path);
    const queryClient = useQueryClient();
    const environmentsQuery = useQuery({ queryKey: ['environments'], queryFn: api.environments });
    const environments = useMemo(() => environmentsQuery.data?.environments ?? [], [environmentsQuery.data]);
    const [lastVault, setLastVault] = useState<string | null>(() => remembered('last'));
    const [toast, setToast] = useState<{ message: string; action?: ToastAction; at: number } | null>(null);
    const [palette, setPalette] = useState<Command[] | null>(null);
    const [shortcuts, setShortcuts] = useState(false);
    const [password, setPassword] = useState(false);
    const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
    const quietUntil = useRef(new Map<string, number>());
    const pageCommands = useRef<() => Command[]>(() => []);

    // Where VARIABLES was last: the module's link, and the environment the edge keeps on other pages. Followed while
    // rendering (the address is the source), remembered by the browser after.
    if (route.page === 'vault' && lastVault !== path) {
        setLastVault(path);
    }
    useEffect(() => {
        if (lastVault) {
            remember('last', lastVault);
        }
    }, [lastVault]);
    const last = lastVault ? parseRoute(lastVault) : null;
    const currentSlug = route.page === 'vault' ? route.env : last?.page === 'vault' ? last.env : null;
    const current =
        environments.find((environment) => environment.slug === currentSlug) ?? (route.page === 'vault' ? null : (environments[0] ?? null));
    const currentSection = route.page === 'vault' ? route.section : last?.page === 'vault' ? last.section : null;
    const lastIsValid = Boolean(
        last?.page === 'vault' &&
        environments.some(
            (environment) => environment.slug === last.env && (!last.section || environment.sections.some((s) => s.slug === last.section)),
        ),
    );
    const vaultLink = lastIsValid && lastVault ? lastVault : current ? vaultPath(current.slug) : '/';
    const paths = useMemo<Record<ModuleName, string>>(
        () => ({ vault: vaultLink, compare: currentSection ? comparePath(currentSection) : '/compare', activity: '/activity' }),
        [vaultLink, currentSection],
    );

    useEffect(() => {
        if (route.page === 'home' && environmentsQuery.isSuccess && environments.length > 0) {
            navigate(paths.vault === '/' ? vaultPath((environments[0] as EnvironmentRef).slug) : paths.vault, { replace: true });
        }
    }, [route.page, environmentsQuery.isSuccess, environments, paths.vault]);

    // The tab's title: where one is.
    useEffect(() => {
        const section = current?.sections.find((item) => item.slug === currentSection);
        const titles: Record<string, string> = { compare: 'Compare', activity: 'Activity', team: 'Team', environments: 'Environments' };
        const where = route.page === 'vault' && current ? `${section ? `${section.name} · ` : ''}${current.name}` : titles[route.page];
        document.title = where ? `${where} · Fleequid Secrets` : 'Fleequid Secrets';
    }, [route.page, current, currentSection]);

    const say = useCallback((message: string, action?: ToastAction) => setToast({ message, action, at: Date.now() }), []);
    useEffect(() => {
        if (!toast) {
            return;
        }
        const timer = setTimeout(() => setToast(null), toast.action ? 6000 : 2800);

        return () => clearTimeout(timer);
    }, [toast]);

    const confirmChange = useCallback((environment: EnvironmentRef, what: string): Promise<string | null> => {
        if (!environment.protected) {
            return Promise.resolve(null);
        }
        if ((quietUntil.current.get(environment.id) ?? 0) > Date.now()) {
            return Promise.resolve(environment.slug);
        }

        return new Promise<string>((resolve, reject) => setConfirm({ environment, what, resolve, reject }));
    }, []);

    // Another environment from the pill, the palette or E: the same section there when it has one.
    const switchEnv = useCallback(
        (slug: string) => {
            const target = environments.find((environment) => environment.slug === slug);
            if (!target) {
                return;
            }
            const same = target.sections.find((section) => section.slug === currentSection);
            navigate(vaultPath(target.slug, same?.slug ?? null));
        },
        [environments, currentSection],
    );
    const openKey = useCallback(
        (entry: KeyIndexEntry) => {
            for (const environment of environments) {
                const section = environment.sections.find((item) => item.id === entry.sectionId);
                if (section) {
                    navigate(vaultPath(environment.slug, section.slug, entry.key));

                    return;
                }
            }
        },
        [environments],
    );
    const copyKey = useCallback(
        (entry: KeyIndexEntry) => {
            api.reveal(entry.sectionId, [entry.variableId], 'copy')
                .then(({ values }) => copyText(values[entry.variableId] ?? ''))
                .then(() => say(`Copied ${entry.key}`))
                .then(() => queryClient.invalidateQueries({ queryKey: ['activity'] }))
                .catch((error: unknown) => say(messageOf(error)));
        },
        [say, queryClient],
    );
    const setPageCommands = useCallback((commands: () => Command[]) => {
        pageCommands.current = commands;
    }, []);
    const openPalette = useCallback(() => {
        const global: Command[] = [
            ...MODULES.map((module) => ({
                icon: 'arrow' as const,
                label: `Go to ${module.label}`,
                key: `G ${module.key.toUpperCase()}`,
                run: () => navigate(paths[module.name]),
            })),
            ...(props.user.role === 'admin'
                ? [
                      { icon: 'layers' as const, label: 'Manage the environments', run: () => navigate('/environments') },
                      { icon: 'users' as const, label: 'Manage the team', run: () => navigate('/team') },
                  ]
                : []),
            ...environments
                .filter((environment) => environment.id !== current?.id)
                .map((environment) => ({ icon: 'right' as const, label: `Switch to ${environment.name}`, run: () => switchEnv(environment.slug) })),
            { icon: 'keyboard', label: 'Keyboard shortcuts', key: '?', run: () => setShortcuts(true) },
            { icon: 'key', label: 'Change my password', run: () => setPassword(true) },
            { icon: 'logout', label: 'Sign out', run: props.onLogout },
        ];
        setPalette([...pageCommands.current(), ...global]);
    }, [paths, props.user.role, props.onLogout, environments, current, switchEnv]);

    // Keys that work on every page: ⌘K, ?, E, and G then a module's letter.
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                if (palette) {
                    setPalette(null);
                } else {
                    openPalette();
                }

                return;
            }
            if (palette || shortcuts || password || confirm || typing(event) || event.metaKey || event.ctrlKey || event.altKey) {
                if (event.key === 'Escape') {
                    setShortcuts(false);
                    setPassword(false);
                }

                return;
            }
            if (goPending()) {
                clearGo();
                const module = MODULES.find((item) => item.key === event.key);
                if (module) {
                    event.preventDefault();
                    navigate(paths[module.name]);
                }

                return;
            }
            if (event.key === 'g') {
                markGo();
            } else if (event.key === '?') {
                event.preventDefault();
                setShortcuts(true);
            } else if (event.key === 'e' && environments.length > 1) {
                const index = environments.findIndex((environment) => environment.id === current?.id);
                switchEnv((environments[(index + 1) % environments.length] as EnvironmentRef).slug);
            }
        };
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [palette, shortcuts, password, confirm, openPalette, paths, environments, current, switchEnv]);

    const shell = useMemo<Shell>(
        () => ({ user: props.user, environments, current, say, confirmChange, setPageCommands, openPalette }),
        [props.user, environments, current, say, confirmChange, setPageCommands, openPalette],
    );

    const page = ((): ReactNode => {
        if (!environmentsQuery.isSuccess) {
            return environmentsQuery.isError ? <Placeholder title="The vault did not answer" text={messageOf(environmentsQuery.error)} /> : null;
        }
        switch (route.page) {
            case 'home':
                return environments.length === 0 ? <Vault env="" section={null} /> : null;
            case 'vault':
                return <Vault env={route.env} section={route.section} />;
            case 'compare':
                return <Compare />;
            case 'activity':
                return <Activity />;
            case 'team':
                return props.user.role === 'admin' ? <Team /> : <Placeholder title="Team" text="Only an admin manages the team." />;
            case 'environments':
                return props.user.role === 'admin' ? (
                    <Environments />
                ) : (
                    <Placeholder title="Environments" text="Only an admin manages the environments." />
                );
            case 'missing':
                return <Placeholder title="Not here" text="There is no page at this address." action={{ label: 'Back to the variables', to: '/' }} />;
        }
    })();
    const active: ModuleName | null =
        route.page === 'vault' || route.page === 'home' ? 'vault' : route.page === 'compare' || route.page === 'activity' ? route.page : null;

    return (
        <ShellContext.Provider value={shell}>
            <div className="shell" style={envStyle(route.page === 'vault' ? current : null)}>
                <div className="edge" />
                <TopBar
                    active={active}
                    paths={paths}
                    current={current}
                    environments={environments}
                    onEnv={switchEnv}
                    user={props.user}
                    onFind={openPalette}
                    onLogout={props.onLogout}
                    onShortcuts={() => setShortcuts(true)}
                    onPassword={() => setPassword(true)}
                />
                {page}
            </div>
            {palette && (
                <Palette
                    environments={environments}
                    commands={palette}
                    onOpenKey={openKey}
                    onCopyKey={copyKey}
                    onOpenSection={(environment, slug) => navigate(vaultPath(environment.slug, slug))}
                    onClose={() => setPalette(null)}
                />
            )}
            {shortcuts && <Shortcuts onClose={() => setShortcuts(false)} />}
            {password && (
                <PasswordDialog
                    onClose={() => setPassword(false)}
                    onDone={() => {
                        setPassword(false);
                        say('Password changed · your other sessions are signed out');
                    }}
                />
            )}
            {confirm && (
                <ConfirmDialog
                    environment={confirm.environment}
                    what={confirm.what}
                    onConfirm={(quiet) => {
                        if (quiet) {
                            quietUntil.current.set(confirm.environment.id, Date.now() + QUIET_MS);
                        }
                        confirm.resolve(confirm.environment.slug);
                        setConfirm(null);
                    }}
                    onCancel={() => {
                        confirm.reject(new Cancelled());
                        setConfirm(null);
                    }}
                />
            )}
            {toast && (
                <div className="toast" key={toast.at} role="status">
                    {toast.message}
                    {toast.action && (
                        <button
                            onClick={() => {
                                toast.action?.run();
                                setToast(null);
                            }}
                        >
                            {toast.action.label}
                        </button>
                    )}
                </div>
            )}
        </ShellContext.Provider>
    );
}
